import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { fmt, pct, biasColor } from "@/lib/format";
import { MarketingLayout } from "@/components/MarketingLayout";
import { Seo } from "@/components/Seo";
import { SignalCard } from "@/components/SignalCard";
import { DemoBadge } from "@/components/DemoBadge";
import { PriceChart } from "@/components/PriceChart";
import {
  ArrowRight, Brain, Radar, Wallet, History, Calculator, ShieldCheck,
  BarChart3, Globe, Zap, LineChart, Layers,
} from "lucide-react";

const features = [
  { icon: Radar, title: "AI Signal Engine", desc: "Probabilistic BUY / SELL / WAIT setups scored 0–100 with entry, stop, targets and risk/reward." },
  { icon: Brain, title: "AI Market Analyst", desc: "GPT-powered explanations grounded strictly on structured market data — never invented prices." },
  { icon: Wallet, title: "Paper Trading", desc: "Practise with $100,000 of simulated capital. Real mechanics, zero real-money risk." },
  { icon: History, title: "Backtesting", desc: "Test strategies across timeframes with equity curves, win rate, drawdown and profit factor." },
  { icon: Calculator, title: "Risk Management", desc: "Position sizing, stop-loss, exposure and portfolio risk tools built for discipline." },
  { icon: Layers, title: "Multi-Timeframe", desc: "Alignment across 15m / 1H / 4H / 1D with STRONG / MODERATE / WEAK confluence scoring." },
];

const markets = [
  ["Crypto", "BTC · ETH · SOL · XRP"],
  ["Forex", "EUR/USD · GBP/USD · USD/JPY"],
  ["Stocks", "NVDA · AAPL · TSLA · MSFT"],
  ["Indices", "S&P 500 · NASDAQ · DAX"],
  ["Commodities", "Gold · Silver · Oil"],
];

export default function Home() {
  const [assets, setAssets] = useState([]);
  const [signals, setSignals] = useState([]);
  const [candles, setCandles] = useState([]);

  useEffect(() => {
    api.get("/markets").then((r) => setAssets(r.data.assets.slice(0, 6))).catch(() => {});
    api.get("/signals?limit=3&min_confidence=55").then((r) => setSignals(r.data.signals.slice(0, 3))).catch(() => {});
    api.get("/markets/BTC%2FUSD/candles?timeframe=4h&bars=120").then((r) => setCandles(r.data.candles)).catch(() => {});
  }, []);

  return (
    <MarketingLayout>
      <Seo title="Understand Markets. Find Signals. Manage Risk." />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hero-glow" />
        <div className="absolute inset-0 grid-noise opacity-40" />
        <div className="relative mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:py-24">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3 py-1 text-xs font-medium text-muted-foreground">
              <Zap className="h-3.5 w-3.5 text-accent" /> AI-powered market intelligence <DemoBadge />
            </div>
            <h1 className="mt-5 font-display text-4xl font-extrabold leading-none tracking-tight sm:text-5xl lg:text-6xl">
              Understand Markets.<br /><span className="text-primary">Find Signals.</span><br />Manage Risk.
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground">
              KREONIS combines market data, technical analysis and artificial intelligence to help you understand
              market conditions, discover probabilistic trading setups and practise with powerful risk-management tools.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" data-testid="hero-start-free" className="group inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/25 transition-all hover:shadow-primary/40">
                Start Free <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/signals" data-testid="hero-explore-signals" className="inline-flex items-center gap-2 rounded-xl border border-border bg-card/60 px-6 py-3 text-sm font-semibold hover:bg-secondary">
                Explore AI Signals
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-bullish" /> No fund custody</span>
              <span className="flex items-center gap-1.5"><Globe className="h-4 w-4 text-accent" /> International markets</span>
              <span className="flex items-center gap-1.5"><BarChart3 className="h-4 w-4 text-primary" /> Transparent track record</span>
            </div>
          </motion.div>

          {/* Demo dashboard preview */}
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }}
            className="rounded-3xl border border-border bg-card p-4 shadow-2xl shadow-primary/5">
            <div className="flex items-center justify-between border-b border-border pb-3">
              <div>
                <div className="flex items-center gap-2 font-display text-lg font-bold">BTC/USD <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">Crypto</span></div>
                <div className="text-xs text-muted-foreground">4H · demo engine</div>
              </div>
              <DemoBadge />
            </div>
            <div className="mt-2"><PriceChart candles={candles} height={200} /></div>
            <div className="mt-3 grid grid-cols-3 gap-3">
              {assets.slice(0, 3).map((a) => (
                <div key={a.symbol} className="rounded-xl border border-border p-3">
                  <div className="text-xs font-medium">{a.symbol}</div>
                  <div className="font-mono text-sm font-semibold">{fmt(a.price, 2)}</div>
                  <div className={`text-xs ${a.change_pct >= 0 ? "text-bullish" : "text-bearish"}`}>{pct(a.change_pct)}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* MARKETS STRIP */}
      <section className="border-y border-border bg-card/40">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-px overflow-hidden sm:grid-cols-3 lg:grid-cols-5">
          {markets.map(([name, sub]) => (
            <div key={name} className="bg-background/40 p-5">
              <div className="font-display text-sm font-bold">{name}</div>
              <div className="mt-1 font-mono text-xs text-muted-foreground">{sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* LIVE MARKETS */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <SectionHead eyebrow="Markets" title="A snapshot of global markets" desc="Crypto, forex, stocks, indices and commodities — analysed by the KREONIS engine." />
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {assets.map((a) => (
            <div key={a.symbol} className="flex items-center justify-between rounded-2xl border border-border bg-card p-4">
              <div>
                <div className="font-display font-bold">{a.symbol}</div>
                <div className="text-xs text-muted-foreground">{a.name}</div>
              </div>
              <div className="text-right">
                <div className="font-mono text-sm font-semibold">{fmt(a.price)}</div>
                <div className={`text-xs ${a.change_pct >= 0 ? "text-bullish" : "text-bearish"}`}>{pct(a.change_pct)}</div>
                <div className={`text-[10px] font-semibold uppercase ${biasColor(a.ai_bias)}`}>{a.ai_bias}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SIGNALS */}
      <section className="border-t border-border bg-card/30">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
          <SectionHead eyebrow="AI Signals" title="Probabilistic setups, fully transparent" desc="Every signal shows entry, stop, targets, risk/reward and a confidence score. Confidence is analytical — not a guarantee of profit." />
          <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {signals.map((s) => <SignalCard key={s.asset + s.timeframe} s={s} />)}
          </div>
          <div className="mt-8 text-center">
            <Link to="/signals" className="inline-flex items-center gap-2 rounded-xl border border-border px-6 py-3 text-sm font-semibold hover:bg-secondary">
              View signal transparency <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <SectionHead eyebrow="Platform" title="An institutional toolkit for retail traders" />
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="rounded-2xl border border-border bg-card p-6 transition-colors hover:border-primary/40">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10"><f.icon className="h-5 w-5 text-primary" /></div>
              <h3 className="mt-4 font-display text-lg font-semibold">{f.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-border">
        <div className="relative mx-auto max-w-7xl overflow-hidden px-4 py-20 text-center sm:px-6">
          <div className="absolute inset-0 hero-glow" />
          <div className="relative">
            <h2 className="mx-auto max-w-2xl font-display text-3xl font-bold tracking-tight sm:text-4xl">Start analyzing markets with KREONIS.</h2>
            <p className="mx-auto mt-3 max-w-xl text-muted-foreground">Create a free account, complete onboarding and explore AI signals, paper trading and risk tools in minutes.</p>
            <Link to="/register" data-testid="cta-start-free" className="mt-8 inline-flex items-center gap-2 rounded-xl bg-primary px-8 py-3.5 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/25">
              Start Free <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}

function SectionHead({ eyebrow, title, desc }) {
  return (
    <div className="max-w-2xl">
      <span className="text-xs font-semibold uppercase tracking-widest text-accent">{eyebrow}</span>
      <h2 className="mt-2 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">{title}</h2>
      {desc && <p className="mt-3 text-muted-foreground">{desc}</p>}
    </div>
  );
}
